/**
 * Print an 80mm-oriented HTML document to a thermal / receipt printer.
 *
 * Two transports: the desktop app prints silently to the configured (or OS
 * default) printer; a browser tab renders into an off-screen iframe so no
 * extra popup window blocks the cashier mid-service.
 *
 * Used by purchase-order slips and any other thermal document that is not the
 * customer receipt (which has its own richer builder in printCustomerReceipt).
 * Split out of the KOT printer in F-RETAIL-11 — retail has no kitchen tickets,
 * but the thermal transport itself is still needed.
 */
import { lockExactPageSize } from '@/lib/printPageSize'
import { printDebug } from '@/lib/printDebug'
import { desktopPrint } from '@/lib/printTransport'

/** Opens the browser print dialog for an 80mm-oriented HTML document (thermal / receipt printers). */
export function printThermalHtmlDocument(html: string, paperWidthMm: number = 80): void {
  if (desktopPrint(html, { paperWidthMm })) return
  printViaLargeOffscreenIframe(html, paperWidthMm)
}

function printViaLargeOffscreenIframe(html: string, paperWidthMm: number): void {
  const tag = 'thermal'
  printDebug.log(tag, 'printViaLargeOffscreenIframe() entry', {
    htmlLength: html.length,
    paperWidthMm,
  })
  const iframe = document.createElement('iframe')
  iframe.setAttribute(
    'style',
    [
      'position:fixed',
      'left:-8000px',
      'top:0',
      'width:320px',
      'min-height:12000px',
      'height:12000px',
      'border:0',
      'opacity:0',
      'pointer-events:none',
      'z-index:-1',
    ].join(';')
  )
  iframe.setAttribute('aria-hidden', 'true')
  document.body.appendChild(iframe)
  printDebug.log(tag, 'iframe appended to body')

  const doc = iframe.contentDocument
  const win = iframe.contentWindow
  if (!doc || !win) {
    printDebug.warn(tag, 'contentDocument/contentWindow is null — aborting', {
      doc: !!doc,
      win: !!win,
    })
    document.body.removeChild(iframe)
    return
  }

  doc.open()
  doc.write(html)
  doc.close()
  printDebug.log(tag, 'html written into iframe doc')
  printDebug.attachWindowProbes(tag, win, doc)

  const runPrint = async () => {
    try {
      await printDebug.spanAsync(tag, 'lockExactPageSize', () =>
        lockExactPageSize(doc, paperWidthMm),
      )
      printDebug.snapshot(tag, 'pre-print snapshot', doc, win)
      win.focus()
      printDebug.span(tag, 'win.print()', () => win.print())
    } catch (err) {
      printDebug.error(tag, 'runPrint threw', err)
    } finally {
      setTimeout(() => {
        printDebug.log(tag, 'cleanup() — removing iframe from DOM (800ms after print fired)')
        try {
          document.body.removeChild(iframe)
        } catch (err) {
          printDebug.warn(tag, 'iframe removal failed', err)
        }
      }, 800)
    }
  }

  requestAnimationFrame(() => {
    setTimeout(runPrint, 300)
  })
}
